import { defineStore } from 'pinia';
import { ref } from 'vue';
import { ElMessage } from 'element-plus';
import {
  createDocumentFromTemplate,
  publishDocumentTemplate,
  templateDialogue,
} from '@/api/documentWorkbenchApi';
import { formatApiErrorMessage } from '@/api/http';
import { useAiAssistantStore } from '@/stores/useAiAssistantStore';
import { useSmartDocumentAiStore } from '@/stores/useSmartDocumentAiStore';
import type {
  DocumentTemplateDetail,
  SmartDocumentTemplateDraftStructured,
  TemplateDialogueDraft,
  TemplateDialogueRequest,
} from '@/types/documentWorkbench';
import {
  isSmartDocumentScenario,
  resolveSmartDocumentScenario,
} from '@/utils/aiSessionContext';
import { thoughtStepsToJson, type ThoughtStep } from '@/utils/aiThought';

const GUIDE_CREATE =
  '我们来创建一份个人文档模版。请尽量补充：①模版名称；②文档类型（需求/评审/会议/SOR 等）；'
  + '③章节大纲；④关键占位符（如「项目名称」「负责人」）。'
  + '模版只保留结构与通用说明，不要写入具体项目事实。';

function isCreateTemplateIntent(text: string): boolean {
  const t = text.trim();
  if (!t) return false;
  return /(?:创建|新建|做一个|帮我做)(?:一个|一份)?(?:模版|模板)/i.test(t)
    || /(?:模版|模板).{0,8}(?:创建|新建)/i.test(t)
    || /与\s*AI\s*对话创建(?:模版|模板)/.test(t)
    || /对话创建(?:模版|模板)/.test(t);
}

function isAbortError(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false;
  const any = e as { code?: string; name?: string; message?: string };
  return any.code === 'ERR_CANCELED'
    || any.name === 'CanceledError'
    || any.name === 'AbortError'
    || /cancel|abort/i.test(String(any.message || ''));
}

export { isCreateTemplateIntent };

export const useSmartDocumentTemplateAuthorStore = defineStore('smartDocumentTemplateAuthor', () => {
  const active = ref(false);
  const conversationId = ref('');
  const sending = ref(false);
  const pendingDraft = ref<TemplateDialogueDraft | null>(null);
  const pendingMessageId = ref<string | null>(null);
  const history = ref<Array<{ role: string; content: string }>>([]);
  const abortController = ref<AbortController | null>(null);
  const lastSavedTemplate = ref<DocumentTemplateDetail | null>(null);

  const boundScenario = ref('');
  const boundSessionId = ref('');

  const aiStore = useAiAssistantStore();
  const smartDocAi = useSmartDocumentAiStore();

  function bindPanelContext(ctx: { scenario: string; sessionId: string }) {
    boundScenario.value = (ctx.scenario || '').trim();
    boundSessionId.value = (ctx.sessionId || '').trim();
  }

  function resolveScenario(): string {
    if (boundScenario.value && isSmartDocumentScenario(boundScenario.value)) {
      return boundScenario.value;
    }
    const scenario = resolveSmartDocumentScenario(smartDocAi.workspaceId || '0');
    if (!isSmartDocumentScenario(aiStore.currentScenario) || aiStore.currentScenario !== scenario) {
      aiStore.currentScenario = scenario;
    }
    return scenario;
  }

  function addAuthorMessage(
    role: 'user' | 'assistant',
    content: string,
    structured?: Record<string, unknown> | null,
    thought?: string,
  ) {
    const scenario = resolveScenario();
    const sessionId = boundSessionId.value || undefined;
    if (sessionId) {
      return aiStore.addMessage(scenario, sessionId, role, content, undefined, thought, structured);
    }
    return aiStore.addMessage(scenario, role, content, undefined, thought, structured);
  }

  function buildDraftStructured(
    draft: TemplateDialogueDraft,
    status: SmartDocumentTemplateDraftStructured['status'],
    templateId?: string,
  ): SmartDocumentTemplateDraftStructured {
    return {
      kind: 'smart_document_template_draft',
      status,
      title: draft.title,
      documentType: draft.documentType,
      description: draft.description,
      usageScenario: draft.usageScenario,
      outlineChapters: draft.outlineChapters,
      contentHtml: draft.contentHtml,
      templateId,
    };
  }

  async function beginTemplateAuthoring() {
    conversationId.value = '';
    pendingDraft.value = null;
    pendingMessageId.value = null;
    history.value = [];
    lastSavedTemplate.value = null;
    active.value = true;
    resolveScenario();
    addAuthorMessage('assistant', GUIDE_CREATE);
    history.value.push({ role: 'assistant', content: GUIDE_CREATE });
  }

  function endTemplateAuthoring() {
    active.value = false;
    conversationId.value = '';
    pendingDraft.value = null;
    pendingMessageId.value = null;
    history.value = [];
    abortController.value = null;
  }

  function cancelSending() {
    abortController.value?.abort();
    abortController.value = null;
    sending.value = false;
  }

  function patchPendingStructured(
    status: SmartDocumentTemplateDraftStructured['status'],
    templateId?: string,
  ) {
    const msgId = pendingMessageId.value;
    if (!msgId || !pendingDraft.value) return;
    const scenario = resolveScenario();
    const sessionId = boundSessionId.value || undefined;
    aiStore.updateMessage(
      scenario,
      msgId,
      {
        structuredResult: buildDraftStructured(
          pendingDraft.value,
          status,
          templateId,
        ) as unknown as Record<string, unknown>,
      },
      sessionId,
    );
  }

  async function sendUserMessage(
    text: string,
    opts?: {
      signal?: AbortSignal;
      onThought?: (steps: ThoughtStep[]) => void;
    },
  ): Promise<{ aborted?: boolean }> {
    const normalized = text.trim();
    if (!normalized || sending.value) return {};
    resolveScenario();
    active.value = true;
    sending.value = true;

    const localAbort = new AbortController();
    abortController.value = localAbort;
    const onAbort = () => localAbort.abort();
    opts?.signal?.addEventListener('abort', onAbort);

    addAuthorMessage('user', normalized);
    history.value.push({ role: 'user', content: normalized });

    const steps: ThoughtStep[] = [
      { key: 'ta_understand', icon: '⏳', title: '理解需求', text: '正在分析你的模版描述…' },
    ];
    opts?.onThought?.([...steps]);

    const progressTimer = window.setTimeout(() => {
      if (localAbort.signal.aborted) return;
      steps[0] = { ...steps[0], icon: '✅', text: '已理解模版目标' };
      steps.push({
        key: 'ta_design',
        icon: '⏳',
        title: '设计模版',
        text: '正在整理名称、章节大纲与占位符…',
      });
      opts?.onThought?.([...steps]);
    }, 500);

    try {
      const body: TemplateDialogueRequest = {
        conversationId: conversationId.value || undefined,
        userMessage: normalized,
        history: history.value.slice(0, -1),
      };
      const result = await templateDialogue(smartDocAi.workspaceId || '0', body);
      if (localAbort.signal.aborted) {
        return { aborted: true };
      }
      if (result.conversationId) {
        conversationId.value = result.conversationId;
      }
      const reply = result.assistantReply || '已收到，请继续补充。';
      history.value.push({ role: 'assistant', content: reply });

      steps.forEach((s, i) => {
        steps[i] = { ...s, icon: '✅', text: s.text.replace(/正在|…/g, '').trim() || '已完成' };
      });
      if (result.phase === 'draft_ready') {
        steps.push({
          key: 'ta_ready',
          icon: '✅',
          title: '草稿就绪',
          text: '模版草稿已整理完成，可保存或开始使用',
        });
      } else {
        steps.push({
          key: 'ta_ask',
          icon: '✅',
          title: '继续补充',
          text: '仍需更多信息以完善模版',
        });
      }
      opts?.onThought?.([...steps]);
      const thoughtJson = thoughtStepsToJson(steps);

      if (result.phase === 'draft_ready' && result.draft?.title) {
        pendingDraft.value = result.draft;
        const assistantMsg = addAuthorMessage(
          'assistant',
          reply,
          buildDraftStructured(result.draft, 'pending') as unknown as Record<string, unknown>,
          thoughtJson,
        );
        pendingMessageId.value = assistantMsg.id;
      } else {
        pendingDraft.value = null;
        pendingMessageId.value = null;
        addAuthorMessage('assistant', reply, null, thoughtJson);
      }
      return {};
    } catch (e) {
      if (isAbortError(e) || localAbort.signal.aborted) {
        addAuthorMessage('assistant', '已终止本次任务。');
        return { aborted: true };
      }
      ElMessage.error(formatApiErrorMessage(e));
      addAuthorMessage('assistant', formatApiErrorMessage(e) || '对话失败，请稍后重试');
      return {};
    } finally {
      window.clearTimeout(progressTimer);
      opts?.signal?.removeEventListener('abort', onAbort);
      if (abortController.value === localAbort) {
        abortController.value = null;
      }
      sending.value = false;
    }
  }

  async function confirmSave(messageId?: string): Promise<DocumentTemplateDetail | null> {
    if (!pendingDraft.value) {
      ElMessage.warning('暂无待保存的模版草稿');
      return null;
    }
    const draft = pendingDraft.value;
    void messageId;
    patchPendingStructured('saving');
    sending.value = true;
    try {
      const result = await templateDialogue(smartDocAi.workspaceId || '0', {
        conversationId: conversationId.value || undefined,
        confirmSave: true,
        draft,
      });
      const saved = result.template;
      if (!saved?.id) {
        throw new Error(result.assistantReply || '保存模版失败');
      }
      lastSavedTemplate.value = saved;
      patchPendingStructured('done', saved.id);
      ElMessage.success(`已保存到我的模版 Draft：${saved.title}`);
      endTemplateAuthoring();
      return saved;
    } catch (e) {
      patchPendingStructured('pending');
      ElMessage.error(formatApiErrorMessage(e));
      return null;
    } finally {
      sending.value = false;
    }
  }

  /**
   * 保存 → 发布到 mine → 基于模版创建文档（对齐 Skill「开始使用」）。
   */
  async function startUsing(messageId?: string): Promise<{
    template: DocumentTemplateDetail;
    documentId: string;
  } | null> {
    const saved = await confirmSave(messageId);
    if (!saved) return null;
    sending.value = true;
    try {
      let usable = saved;
      if (saved.status !== 'published') {
        usable = await publishDocumentTemplate(saved.id, { targetScope: 'mine' });
      }
      const created = await createDocumentFromTemplate(
        smartDocAi.workspaceId || '0',
        usable.id,
      );
      smartDocAi.lastAppliedDocumentId = created.documentId;
      ElMessage.success(`已基于模版创建文档：${created.title || usable.title}`);
      return { template: usable, documentId: created.documentId };
    } catch (e) {
      ElMessage.error(formatApiErrorMessage(e));
      return null;
    } finally {
      sending.value = false;
    }
  }

  function continueEditing(messageId?: string) {
    if (messageId && pendingMessageId.value === messageId) {
      patchPendingStructured('cancelled');
    } else if (pendingMessageId.value) {
      patchPendingStructured('cancelled');
    }
    pendingDraft.value = null;
    pendingMessageId.value = null;
    const tip = '好的，请继续说明要修改的地方。';
    addAuthorMessage('assistant', tip);
    history.value.push({ role: 'assistant', content: tip });
  }

  function cancelAuthoring() {
    cancelSending();
    if (pendingMessageId.value) {
      patchPendingStructured('cancelled');
    }
    endTemplateAuthoring();
  }

  return {
    active,
    conversationId,
    sending,
    pendingDraft,
    pendingMessageId,
    history,
    lastSavedTemplate,
    bindPanelContext,
    beginTemplateAuthoring,
    endTemplateAuthoring,
    sendUserMessage,
    confirmSave,
    startUsing,
    continueEditing,
    cancelAuthoring,
    cancelSending,
    isCreateTemplateIntent,
  };
});
